import { CheckCircle2, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import paymentService from '../utils/paymentService'
import saOnlineService from '../utils/saOnlineService'
import selfAssessmentApiService from '../utils/selfAssessmentApiService'
import { AuthService } from '../utils/authService'

export default function PaymentSuccessPage() {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const [status, setStatus] = useState<'verifying' | 'verified'>('verifying')
    const redirectedRef = useRef(false)

    const sessionId = searchParams.get('session_id')
    const planSlug = searchParams.get('planSlug') || searchParams.get('plan')
    const formId = searchParams.get('formId')
    const ninoParam = searchParams.get('nino')
    const taxYearParam = searchParams.get('taxYear')
    const source = searchParams.get('source')
    const flowParam = searchParams.get('flow')
    const assessmentIdParam = searchParams.get('assessmentId')
    const saReturnIdParam = searchParams.get('saReturnId')
    const companyNumberParam = searchParams.get('companyNumber')
    const stepParam = searchParams.get('step')

    const isSaFlow = flowParam === 'sa' ? true : flowParam === 'traditional' ? false : !!planSlug?.includes('sa-')

    // Build redirect URL
    const getRedirectUrl = () => {
        if (source === 'ct600') {
            return formId ? `/ct600-filing/${formId}` : '/ct600-filing'
        }
        if (source === 'companies-house') {
            // Confirmation Statement: land back on Review & Submit (now paid) so
            // the user completes the filing — mirrors the Self Assessment flow.
            const chParams = new URLSearchParams()
            if (companyNumberParam) chParams.set('companyNumber', companyNumberParam)
            chParams.set('step', stepParam || 'review')
            return `/companies-house-filing?${chParams.toString()}`
        }
        const params = new URLSearchParams()
        if (flowParam) params.set('flow', flowParam)
        if (planSlug) params.set('planSlug', planSlug)
        if (taxYearParam) params.set('taxYear', taxYearParam)
        if (ninoParam) params.set('nino', ninoParam)
        if (saReturnIdParam) params.set('saReturnId', saReturnIdParam)
        if (formId) params.set('formId', formId)
        if (assessmentIdParam) params.set('assessmentId', assessmentIdParam)
        params.set('fromDraft', 'true')
        // Land directly on the Submit step (6) — payment is done, so the user
        // enters their Government Gateway details there and submits.
        params.set('step', '6')
        return `/self-assessment/filing?${params.toString()}`
    }

    const doRedirect = () => {
        if (redirectedRef.current) return
        redirectedRef.current = true
        setStatus('verified')
        // Brief delay so the user sees the checkmark flash
        setTimeout(() => navigate(getRedirectUrl()), 800)
    }

    useEffect(() => {
        // Payment status and submission outcome are reconciled from the backend on
        // the filing page (checkFormPayment + getReturn) — no localStorage flow-state.

        // Run background verification then redirect
        if (sessionId) {
            verifyPaymentSession()
                .catch(err => {
                    console.warn('Background payment verification failed (non-critical):', err)
                })
                .finally(() => {
                    doRedirect()
                })
        } else {
            // No session to verify — redirect immediately
            doRedirect()
        }

        // Safety: redirect after 4s even if verification hangs
        const safety = setTimeout(() => doRedirect(), 4000)
        return () => clearTimeout(safety)
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const verifyPaymentSession = async () => {
        try {
            if (!AuthService.isAuthenticated()) {
                console.warn('User not authenticated after payment redirect - skipping API verification')
                return
            }

            // Verify Stripe session (optional)
            try {
                await paymentService.verifySession(sessionId!, { skipAuthLogout: true })
            } catch (verifyError) {
                console.warn('Session verification failed (non-critical):', verifyError)
            }

            // Verify subscription status, then auto-submit if paid. Payment + submission
            // status are read back from the backend on the filing page.
            if (formId) {
                try {
                    const subscriptionResult = await paymentService.checkFormPayment(formId, { skipAuthLogout: true })
                    if (subscriptionResult.success && subscriptionResult.data.status === 'paid') {
                        // SA flow: auto-submit with the credentials stashed before checkout.
                        if (isSaFlow && (saReturnIdParam || formId)) {
                            const returnId = saReturnIdParam || formId
                            const credentialsData = sessionStorage.getItem('gg_credentials')
                            if (credentialsData) {
                                try {
                                    const credentials = JSON.parse(credentialsData)
                                    const credentialsAge = Date.now() - credentials.timestamp
                                    if (credentialsAge > 30 * 60 * 1000) {
                                        sessionStorage.removeItem('gg_credentials')
                                    } else {
                                        // Frontend always requests 'live'; the backend resolves
                                        // the real target. The resulting status lives on the
                                        // backend and is reconciled by the filing page.
                                        await saOnlineService.submitReturn(
                                            returnId,
                                            {
                                                saReturnId: returnId,
                                                governmentGatewayUserId: credentials.userId,
                                                governmentGatewayPassword: credentials.password,
                                                environment: 'live',
                                            },
                                            { skipAuthLogout: true }
                                        )
                                        sessionStorage.removeItem('gg_credentials')
                                    }
                                } catch (submitError: any) {
                                    console.error('Failed to submit SA return (non-critical):', submitError)
                                    sessionStorage.removeItem('gg_credentials')
                                }
                            }
                        }

                        // Traditional MTD: auto-submit final declaration.
                        if (!isSaFlow && ninoParam && taxYearParam && assessmentIdParam) {
                            try {
                                await selfAssessmentApiService.triggerCalculation(
                                    ninoParam,
                                    taxYearParam,
                                    true,
                                    assessmentIdParam,
                                    { skipAuthLogout: true }
                                )
                            } catch (submitError: any) {
                                console.error('Failed to submit Traditional MTD final declaration (non-critical):', submitError)
                            }
                        }
                    }
                } catch (subError: any) {
                    console.error('Subscription check failed (non-critical):', subError)
                }
            }
        } catch (error: any) {
            console.error('Payment verification failed (non-critical):', error)
        }
    }

    return (
        <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
            <div className='text-center space-y-4'>
                {status === 'verifying' ? (
                    <>
                        <Loader2 className='mx-auto h-10 w-10 animate-spin text-gray-900' />
                        <p className='text-sm font-medium text-gray-700'>Confirming your payment...</p>
                        <p className='text-xs text-gray-500'>You'll be redirected back to your filing shortly.</p>
                    </>
                ) : (
                    <>
                        <CheckCircle2 className='mx-auto h-10 w-10 text-green-600' />
                        <p className='text-sm font-medium text-gray-700'>Payment confirmed</p>
                        <p className='text-xs text-gray-500'>Redirecting...</p>
                    </>
                )}
            </div>
        </div>
    )
}
